import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import {
    SnackbarContent,
    IconButton,
    Stack,
    Box,
    Typography
} from '@sumologic/yama-ds-components';
import {
    CheckIcon,
    AlertCircleIcon,
    AlertTriangleIcon,
    LightbulbIcon,
    XIcon
} from '@sumologic/yama-ds-icons';

export const stickyAlertBaseClassname = 'sticky-alert';
export const stickyAlertDefaultStatus = 'info';
export const stickyAlertDefaultDelay = 6e3;
export const stickyAlertStatuses = {
    info: stickyAlertDefaultStatus,
    danger: 'danger',
    warning: 'warning',
    success: 'success'
};

const STATUS_CONFIG = {
    success: { color: 'success', Icon: CheckIcon },
    danger: { color: 'error', Icon: AlertCircleIcon },
    warning: { color: 'warning', Icon: AlertTriangleIcon },
    info: { color: 'info', Icon: LightbulbIcon }
};

const StickyAlert = ({
    status,
    delay = stickyAlertDefaultDelay,
    closeAfterDelay = true,
    onClose,
    title,
    subTitle,
    message,
    show = false
}) => {
    const ref = useRef({}).current;
    const [isClosed, setIsClosed] = useState(false);

    useEffect(() => {
        ref.isMounted = true;
        return () => {
            ref.isMounted = false;
        };
    }, []);

    const handleClose = useCallback(() => {
        onClose?.();
        setIsClosed(true);
    }, [onClose]);

    // Auto-hide timer
    useEffect(() => {
        if (!show || isClosed || !closeAfterDelay || !delay) return;

        const timer = setTimeout(() => {
            if (ref.isMounted) {
                handleClose();
            }
        }, delay);

        return () => clearTimeout(timer);
    }, [show, isClosed, closeAfterDelay, delay, handleClose]);

    if (!show || isClosed) return null;

    const config = STATUS_CONFIG[status] || STATUS_CONFIG.info;
    const { color, Icon } = config;

    const composedMessage = (
        <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box
                sx={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    height: '1.43em'
                }}
            >
                <Icon color="inherit" size="14" />
            </Box>
            <Typography
                variant="body1"
                color="inherit"
                data-sumo-test="toast-text"
            >
                {title && (
                    <Typography
                        component="span"
                        variant="body1"
                        color="inherit"
                        sx={{ fontWeight: 700 }}
                    >
                        {title}
                        {subTitle && ` — ${subTitle}`}
                        {message && ': '}
                    </Typography>
                )}
                {message}
            </Typography>
        </Stack>
    );

    return (
        <SnackbarContent
            className={`${stickyAlertBaseClassname} ${
                status || stickyAlertDefaultStatus
            }`}
            color={color}
            message={composedMessage}
            action={
                <IconButton size="small" aria-label="Close" onClick={handleClose}>
                    <XIcon fontSize="small" />
                </IconButton>
            }
        />
    );
};

{
    const objOrStr = PropTypes.oneOfType([PropTypes.node, PropTypes.string]);
    StickyAlert.propTypes = {
        title: objOrStr,
        subTitle: objOrStr,
        message: objOrStr,
        status: PropTypes.oneOf(Object.values(stickyAlertStatuses)),
        closeAfterDelay: PropTypes.bool,
        delay: PropTypes.number,
        onClose: PropTypes.func
    };
}

export default StickyAlert;
